import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AlertLabelComponent } from '../../../core/labels/alert-label.component';

interface ClaimFlags {
  alert?: boolean;
  alertTextClaim?: string;
  alertTextEvent?: string;
  alertTextClaimant?: string;
  apportionment?: boolean;
  erisa?: boolean;
  claimantRepresented?: boolean;
  midRange?: boolean;
  atRisk?: boolean;
  litigation?: boolean;
  managedCare?: boolean;
  medicare?: boolean;
  triage?: boolean;
  overpaymentBalance?: boolean;
  overpaymentRecovered?: boolean;
  rehabilitation?: boolean;
  relapse?: boolean;
  sifFiled?: boolean;
  sifAccepted?: boolean;
  subOwner?: boolean;
  subrogation?: boolean;
  tender?: boolean;
  excess?: boolean;
  punitiveDamages?: boolean;
}

interface FlagConfig {
  key: keyof ClaimFlags;
  label: string;
  icon: string;
  tooltip?: string;
  condition?: (flags: ClaimFlags) => boolean;
}

@Component({
  selector: 'app-claim-flags',
  imports: [CommonModule, AlertLabelComponent],
  template: `
    <div
      class="tw-flex tw-flex-wrap tw-gap-2 tw-max-w-[380px]"
      role="list"
      aria-label="Claim flags"
    >
      @for (flag of visibleFlags; track flag.key) {
        <div role="listitem">
          <app-alert-label
            [label]="flag.label"
            [icon]="flag.icon"
            [title]="flag.tooltip || ''"
            [ariaLabel]="
              flag.tooltip
                ? flag.label + ' flag: ' + flag.tooltip
                : flag.label + ' flag'
            "
          />
        </div>
      }
    </div>
  `,
  styles: ``,
})
export class ClaimFlagsComponent {
  @Input({ required: true })
  flags!: ClaimFlags;

  private readonly flagConfigs: FlagConfig[] = [
    {
      key: 'alert',
      label: 'Alert',
      icon: 'fa-solid fa-message-exclamation',
      tooltip: '',
    },
    {
      key: 'apportionment',
      label: 'Apportionment',
      icon: 'fa-solid fa-people-arrows',
    },
    {
      key: 'erisa',
      label: 'ERISA',
      icon: 'fa-solid fa-briefcase',
    },
    {
      key: 'claimantRepresented',
      label: 'Claimant Represented',
      icon: 'fa-solid fa-user-group',
    },
    {
      key: 'midRange',
      label: 'Mid Range',
      icon: 'fa-solid fa-person-circle-exclamation',
      tooltip:
        'Claimant has reached the middle of the recommended return to work date range. Please review the Medical screen for more information.',
    },
    {
      key: 'atRisk',
      label: 'At-risk',
      icon: 'fa-solid fa-person-circle-exclamation',
      tooltip:
        'Claimant is within 7 days of the recommended duration for the specific injury/illness. Please review the Medical screen for more information.',
    },
    {
      key: 'litigation',
      label: 'Litigation',
      icon: 'fa-solid fa-scale-balanced',
    },
    {
      key: 'managedCare',
      label: 'Managed Care',
      icon: 'fa-solid fa-suitcase-medical',
    },
    {
      key: 'medicare',
      label: 'Medicare',
      icon: 'fa-solid fa-hand-holding-medical',
    },
    {
      key: 'triage',
      label: 'Triage',
      icon: 'fa-solid fa-user-nurse',
    },
    {
      key: 'overpaymentBalance',
      label: 'OverPaid - Balance',
      icon: 'fa-solid fa-sack-dollar',
    },
    {
      key: 'overpaymentRecovered',
      label: 'OverPaid - Recovered',
      icon: 'fa-solid fa-receipt',
    },
    {
      key: 'rehabilitation',
      label: 'Rehabilitation',
      icon: 'fa-solid fa-book-open-reader',
    },
    {
      key: 'relapse',
      label: 'Relapse',
      icon: 'fa-solid fa-repeat',
    },
    {
      key: 'sifFiled',
      label: 'SIF File',
      icon: 'fa-solid fa-money-check-dollar-pen',
      condition: (flags) =>
        flags.sifFiled === true && flags.sifAccepted !== true,
    },
    {
      key: 'sifAccepted',
      label: 'SIF Accepted',
      icon: 'fa-solid fa-money-check-dollar-pen',
    },
    {
      key: 'subOwner',
      label: 'Sub Owner',
      icon: 'fa-solid fa-people-group',
    },
    {
      key: 'subrogation',
      label: 'Subrogation',
      icon: 'fa-solid fa-hand-point-left',
    },
    {
      key: 'tender',
      label: 'Tender',
      icon: 'fa-solid fa-hand-holding-dollar',
    },
    {
      key: 'excess',
      label: 'Excess',
      icon: 'fa-solid fa-layer-group',
    },
    {
      key: 'punitiveDamages',
      label: 'Punitive Damages',
      icon: 'fa-solid fa-gavel',
    },
  ];

  get visibleFlags(): FlagConfig[] {
    if (!this.flags) {
      return [];
    }

    return this.flagConfigs
      .filter((config) => {
        // Use custom condition if provided
        if (config.condition) {
          return config.condition(this.flags);
        }
        // Otherwise check if the flag is true
        return this.flags[config.key] === true;
      })
      .map((config) => {
        // For alert flag, dynamically set tooltip from alert text
        if (config.key === 'alert') {
          return {
            ...config,
            tooltip: this.getAlertTooltip(),
          };
        }
        return config;
      });
  }

  private getAlertTooltip(): string {
    const alerts: string[] = [];
    if (this.flags.alertTextEvent) {
      alerts.push('EVENT: ' + this.flags.alertTextEvent);
    }
    if (this.flags.alertTextClaim) {
      alerts.push('CLAIM: ' + this.flags.alertTextClaim);
    }
    if (this.flags.alertTextClaimant) {
      alerts.push('CLAIMANT: ' + this.flags.alertTextClaimant);
    }
    return alerts.join('\n\n');
  }
}
